import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { LoadingState } from '@usf/ngrx-product';
import { Product } from '@shared/models/product.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { PlatformEnum } from '@panamax/app-state';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import { AIListBuilderService } from '../../services/ai-list-builder.service';

export interface IngredientMenuItem {
  id: string;
  name: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-ai-list-ingredient-menu',
  templateUrl: './ai-list-ingredient-menu.component.html',
  styleUrls: ['./ai-list-ingredient-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListIngredientMenuComponent implements OnChanges {
  @Input() title = '';
  @Input() searchPlaceholder = 'Search For Product Name';
  @Input() platformType: PlatformEnum;
  @Input() filterOptions: FilterOption[] = [
    { id: 'direct', label: 'i18n.lists.direct', selected: false },
    {
      id: 'early-cutoff',
      label: 'i18n.lists.earlyCutoff',
      selected: false,
    },
    {
      id: 'discontinued',
      label: 'i18n.lists.discontinuedWhenOut',
      selected: false,
    },
    {
      id: 'exclusive',
      label: 'i18n.lists.exclusiveBrands',
      selected: false,
    },
  ];
  @Input() ingredients: Product[] = [];
  @Input() matchingProductNumber: number | null = null;
  @Input() loadingState: LoadingState = LoadingState.loading;
  @Input() loaded = false;
  @Input() loading = false;

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() save: EventEmitter<Product> = new EventEmitter();
  @Output() filter: EventEmitter<void> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter();

  platformRef = PlatformEnum;
  searchQuery = '';
  previousSearchQuery = '';
  showFilterView = false;
  selectedFilterCount = 0;

  // Reactive state for selected product
  private selectedProductSubject = new BehaviorSubject<Product | null>(null);
  selectedProduct$ = this.selectedProductSubject.asObservable();

  // Add a new property to track if filters have been saved before
  private hasFiltersBeenSavedBefore = false;

  constructor(private aiListBuilderService: AIListBuilderService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Auto-select matching product when ingredients change
    if (
      (changes.ingredients || changes.matchingProductNumber) &&
      this.ingredients?.length > 0
    ) {
      // If there's a matching product and nothing is selected yet
      if (
        this.matchingProductNumber &&
        !this.selectedProductSubject.getValue()
      ) {
        const matchingProduct = this.ingredients.find(
          ingredient => ingredient.productNumber === this.matchingProductNumber,
        );

        if (matchingProduct) {
          this.selectedProductSubject.next(matchingProduct);
        }
      }
    }
  }

  onCancel(): void {
    // Reset all state regardless of which menu we're in
    this.resetAllState();

    // Always emit cancel to ensure everything is closed
    // Clear selected product before closing
    this.selectedProductSubject.next(null);
    this.cancel.emit();
  }

  /**
   * Shows the filter view and prepares filter options
   */
  onFilter(): void {
    // Show the filter view
    this.showFilterView = true;

    // Emit the filter event for any parent components that need it
    this.filter.emit();
  }

  /**
   * Toggle the selected state of a filter option
   * @param option The filter option to toggle
   */
  toggleFilterOption(option: FilterOption) {
    option.selected = !option.selected;
    // We don't update the selectedFilterCount here
    // It's only updated when Save is clicked
  }

  /**
   * Applies the current filter options to the alternative ingredients
   * Updates the filter state in the store
   */
  applyFilterOptions(): void {
    const menuFilters: string[] = [];

    // Collect all selected filter IDs
    for (const option of this.filterOptions) {
      if (option.selected) {
        menuFilters.push(option.id);
      }
    }

    // Update filter options in the store to filter the ingredients
    this.aiListBuilderService.updateMenuFilterOptions(menuFilters);

    // Force re-filter on search results by re-applying the current search
    this.searchInIngredientMenu(this.searchQuery, true);
  }

  /**
   * Handles the save button click
   * @param event The click event
   */
  onSave(event?: Event): void {
    // Prevent default behavior if event exists
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.showFilterView) {
      // We're in filter view, so this is a filter save action
      console.log('Saving filter options');
      this.saveFilterOptions();
    } else {
      // We're in the main product selection view
      this.saveProductSelection();
    }
  }

  /**
   * Determines if the save button should be enabled in the filter view
   * @returns True if save button should be enabled, false otherwise
   */
  shouldEnableSaveInFilterView(): boolean {
    // Enable save if any options are selected
    if (this.filterOptions.some(option => option.selected)) {
      return true;
    }

    // If no options are selected but filters have been saved before,
    // we should still enable the save button to allow clearing all filters
    return this.hasFiltersBeenSavedBefore && this.selectedFilterCount > 0;
  }

  /**
   * Handles saving filter options
   */
  private saveFilterOptions(): void {
    // Update the filter count
    this.updateSelectedFilterCount();

    // Mark that filters have been saved before
    this.hasFiltersBeenSavedBefore = true;

    // Apply the selected filters
    this.applyFilterOptions();

    // Hide the filter view and return to the main ingredient menu
    this.showFilterView = false;
  }

  /**
   * Handles saving the selected product
   */
  private saveProductSelection(): void {
    const selectedProduct = this.selectedProductSubject.getValue();
    if (selectedProduct) {
      // Save the product then reset all state afterward
      this.save.emit(selectedProduct);
    }
  }

  onSearch(event: any): void {
    // Handle ion-searchbar's ionInput event
    const query = event.target.value || '';
    this.searchQuery = query;

    // Search in the ingredient menu with the new query
    this.searchInIngredientMenu(this.searchQuery);

    // Also emit the search term for any parent components that need it
    this.search.emit(this.searchQuery);
  }

  /**
   * Handles searching in the ingredient menu
   * @param searchKey - The search term to filter by
   * @param forceSearch - Whether to force search even if the term hasn't changed
   */
  searchInIngredientMenu(
    searchKey: string,
    forceSearch: boolean = false,
  ): void {
    if (forceSearch || searchKey !== this.previousSearchQuery) {
      console.log('searchKey', searchKey);
      this.aiListBuilderService.searchInIngredientMenu(searchKey);
      this.previousSearchQuery = searchKey;
    }
  }

  onProductSelected(product: Product): void {
    this.selectedProductSubject.next(product);
  }

  isProductSelected(product: Product): boolean {
    const selectedProduct = this.selectedProductSubject.getValue();
    return selectedProduct?.productNumber === product.productNumber;
  }

  /**
   * Check if a product matches the current ingredient being edited
   */
  isMatchingProduct(
    product: Product,
    matchingProductNumber: number | null,
  ): boolean {
    return (
      matchingProductNumber !== null &&
      product.productNumber === matchingProductNumber
    );
  }

  /**
   * Checks if any filter options have been selected
   * @returns True if any filter is selected, false otherwise
   */
  hasFilterChanges(): boolean {
    if (!this.filterOptions) {
      return false;
    }

    // Return true if any option is selected
    return this.filterOptions.some(option => option.selected);
  }

  clearAllFilters() {
    this.filterOptions.forEach(option => (option.selected = false));
    // We don't update the selectedFilterCount here
    // It stays the same until Save is clicked
  }

  private resetAllState(): void {
    // Reset product selection but don't clear the products list
    this.selectedProductSubject.next(null);

    // Reset filter view and filters
    this.showFilterView = false;
    this.filterOptions.forEach(option => (option.selected = false));

    // Reset search
    this.searchQuery = '';
    this.previousSearchQuery = '';

    // Reset the filter count too since we're fully closing all menus
    this.selectedFilterCount = 0;
  }

  private updateSelectedFilterCount() {
    this.selectedFilterCount = this.filterOptions.filter(
      o => o.selected,
    ).length;
  }

  // Method for back arrow to return to main menu without saving filter changes
  resetFilterView(): void {
    // Hide the filter view and return to main menu without changing filter options
    this.showFilterView = false;
  }
}
